import { gql, TypedDocumentNode } from "@apollo/client";

export const GLOBAL_SEARCH_ENTITY_HITS_PER_PAGE = 10;
// Keystroke-to-query debounce for entity search. Page search stays instant
// (local ranking); only the Backoffice round trip waits.
export const GLOBAL_SEARCH_DEBOUNCE_MS = 150;
// Minimum trimmed query length before firing entity search. Single characters
// match too broadly to be useful and cost a full 4-matcher round trip.
export const GLOBAL_SEARCH_MIN_QUERY_LENGTH = 2;
// Per-type cap on recentlyViewedIds sent with entity search. The server boosts
// at most 25 per type; 20 keeps variables small without losing real recency.
export const GLOBAL_SEARCH_RECENTLY_VIEWED_IDS_PER_TYPE = 20;

export type GlobalSearchTypeFilter = "PEOPLE" | "FACILITIES" | "JOBS" | "ACTIVITIES";

export type GlobalSearchPersonNode = {
  id: string;
  pgaId: string | null;
  displayName: string;
  profilePhoto: string | null;
  primarySection: { id: string | null; name: string | null } | null;
  primaryFacility?: { id: string | null; name: string | null; addresses: { state: string | null }[] } | null;
};

export type GlobalSearchFacilityNode = {
  id: string;
  name: string;
  legacyId: string | null;
  section: { id: string; name: string | null } | null;
  mainAddress: { city: string | null; state: string | null } | null;
};

export type GlobalSearchPeopleSearchResponse = {
  fuzzySearchPeople: { nodes: GlobalSearchPersonNode[] };
};

export type GlobalSearchPeopleSearchVariables = {
  search: string;
  first: number;
  recentlyViewedIds: string[];
};

export type GlobalSearchFacilitiesSearchResponse = {
  fuzzySearchFacilities: { nodes: GlobalSearchFacilityNode[] };
};

export type GlobalSearchFacilitiesSearchVariables = {
  search: string;
  first: number;
  recentlyViewedIds: string[];
};

export type GlobalSearchJobNode = {
  id: string;
  name: string;
  stage: string | null;
  status: string | null;
  owner: { name: string | null } | null;
  facility: {
    id: string | null;
    name: string | null;
    section: { id: string | null; name: string | null } | null;
  } | null;
};

export type GlobalSearchActivityNode = {
  id: string;
  title: string;
  internalDescription: string | null;
  allowParticipationStarting: string | null;
  allowParticipationThrough: string | null;
  activityType: { id: string | null; title: string | null } | null;
  managingSection: { id: string | null; name: string | null } | null;
  event: {
    slug: string | null;
    title: string | null;
    startsAt: string | null;
    endsAt: string | null;
  } | null;
};

export type GlobalSearchHitNode =
  | (GlobalSearchPersonNode & { __typename: "BackofficePerson" })
  | (GlobalSearchFacilityNode & { __typename: "BackofficeFacility" })
  | (GlobalSearchJobNode & { __typename: "BackofficeJobPost" })
  | (GlobalSearchActivityNode & { __typename: "Activity" });

export type GlobalSearchResponse = {
  globalSearch: GlobalSearchHitNode[];
};

export type GlobalSearchVariables = {
  search: string;
  firstPerType: number;
  types?: GlobalSearchTypeFilter[] | null;
  recentlyViewedIds: {
    people: string[];
    facilities: string[];
    jobs: string[];
    activities: string[];
  };
  withCareer: boolean;
};

const PERSON_SELECTION = `
        id
        pgaId
        displayName
        profilePhoto
        primarySection {
          id
          name
        }
`;

const FACILITY_SELECTION = `
        id
        name
        legacyId
        section {
          id
          name
        }
        mainAddress {
          city
          state
        }
`;

export const GLOBAL_SEARCH_PEOPLE_SEARCH_QUERY: TypedDocumentNode<
  GlobalSearchPeopleSearchResponse,
  GlobalSearchPeopleSearchVariables
> = gql`
  query GlobalSearchPeopleSearch($search: String!, $first: Int!, $recentlyViewedIds: [ID!]) {
    fuzzySearchPeople(input: { search: $search }, first: $first, recentlyViewedIds: $recentlyViewedIds) {
      nodes {
        ${PERSON_SELECTION}
      }
    }
  }
`;

export const GLOBAL_SEARCH_FACILITIES_SEARCH_QUERY: TypedDocumentNode<
  GlobalSearchFacilitiesSearchResponse,
  GlobalSearchFacilitiesSearchVariables
> = gql`
  query GlobalSearchFacilitiesSearch($search: String!, $first: Int!, $recentlyViewedIds: [ID!]) {
    fuzzySearchFacilities(
      input: { search: $search, excludeArchived: true }
      first: $first
      recentlyViewedIds: $recentlyViewedIds
    ) {
      nodes {
        ${FACILITY_SELECTION}
      }
    }
  }
`;

const JOB_SELECTION = `
        id
        name
        stage
        status
        owner {
          name
        }
        facility {
          id
          name
          section {
            id
            name
          }
        }
`;

const ACTIVITY_SELECTION = `
        id
        title
        internalDescription
        allowParticipationStarting
        allowParticipationThrough
        activityType {
          id
          title
        }
        managingSection {
          id
          name
        }
        event {
          slug
          title
          startsAt
          endsAt
        }
`;

export const GLOBAL_SEARCH_QUERY: TypedDocumentNode<GlobalSearchResponse, GlobalSearchVariables> = gql`
  query GlobalSearch(
    $search: String!
    $firstPerType: Int!
    $types: [BackofficeGlobalSearchType!]
    $recentlyViewedIds: BackofficeGlobalSearchRecentlyViewedIdsInput
    $withCareer: Boolean!
  ) {
    globalSearch(
      search: $search
      firstPerType: $firstPerType
      types: $types
      recentlyViewedIds: $recentlyViewedIds
    ) {
      __typename
      ... on BackofficePerson {
        ${PERSON_SELECTION}
        primaryFacility @include(if: $withCareer) {
          id
          name
          addresses {
            state
          }
        }
      }
      ... on BackofficeFacility {
        ${FACILITY_SELECTION}
      }
      ... on BackofficeJobPost {
        ${JOB_SELECTION}
      }
      ... on Activity {
        ${ACTIVITY_SELECTION}
      }
    }
  }
`;
